import Symbols from "./mappings/symbols/Symbols";

export const DEFAULT_ICON_SCALE_CM = 100 / 37.7952755906;

export const uuidv4 = () => {
  return ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
    (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
  );
};

const cloneDeep = (value) => {
  if (value === undefined || value === null) return value;
  return JSON.parse(JSON.stringify(value));
};

const toEditorPath = (src) => {
  if (!src || typeof src !== 'string') return src;
  if (src.includes('/assets/editor-symbols/')) return src;
  if (
    src === '/symbol-assets/Bus.svg' ||
    src === '/symbol-assets/BusMultinodo.svg'
  ) return src;
  return src.replace('/symbol-assets/', '/assets/editor-symbols/');
};

const setUniformDragImage = (event) => {
  const dataTransfer = event?.dataTransfer;
  const currentTarget = event?.currentTarget;
  if (!dataTransfer || !currentTarget || typeof document === 'undefined') return;

  const sourceImg = currentTarget.querySelector?.('img');
  if (!sourceImg?.src) return;

  const preview = document.createElement('div');
  preview.style.position = 'fixed';
  preview.style.left = '-1000px';
  preview.style.top = '-1000px';
  preview.style.width = '72px';
  preview.style.height = '72px';
  preview.style.display = 'flex';
  preview.style.alignItems = 'center';
  preview.style.justifyContent = 'center';
  preview.style.background = 'rgba(255, 255, 255, 0.96)';
  preview.style.border = '1px solid rgba(0, 0, 0, 0.18)';
  preview.style.borderRadius = '6px';
  preview.style.boxShadow = '0 8px 18px rgba(0, 0, 0, 0.16)';
  preview.style.pointerEvents = 'none';

  const img = document.createElement('img');
  img.src = sourceImg.src;
  img.alt = '';
  img.style.width = '54px';
  img.style.height = '54px';
  img.style.objectFit = 'contain';
  img.style.transform = sourceImg.style.transform || getComputedStyle(sourceImg).transform;
  img.style.transformOrigin = 'center';

  preview.appendChild(img);
  document.body.appendChild(preview);
  dataTransfer.setDragImage(preview, 36, 36);
  window.setTimeout(() => {
    preview.remove();
  }, 0);
};

const resolveDeviceEntry = (group, baseAlias, variantAlias = null) => {
  if (!Symbols.hasOwnProperty(group)) {
    return { device: null, baseAlias };
  }

  let device = Symbols[group].find((candidate) => candidate.alias === baseAlias);

  if (!device && variantAlias) {
    device = Symbols[group].find(
      (candidate) =>
        Array.isArray(candidate?.variants) &&
        candidate.variants.some((variant) => variant.alias === variantAlias)
    );
    if (device) {
      baseAlias = device.alias;
    }
  }

  return { device, baseAlias };
};

export const buildSymbolPayload = (
  group,
  baseAlias,
  angleOffset,
  isRotable,
  symbolLabel,
  variantAlias = null,
  scaleCm = DEFAULT_ICON_SCALE_CM
) => {
  const uuid = uuidv4();

  const { device, baseAlias: resolvedBaseAlias } = resolveDeviceEntry(group, baseAlias, variantAlias);
  if (!device) {
    return null;
  }

  const variants = Array.isArray(device.variants)
    ? device.variants.map((variant) => ({ ...variant }))
    : [];

  let activeVariant = null;
  if (variantAlias) {
    activeVariant = variants.find((variant) => variant.alias === variantAlias) || null;
  } else {
    activeVariant =
      variants.find((variant) => variant.selected) ||
      (variants.length > 0 ? variants[0] : null);
  }

  if (activeVariant) {
    variants.forEach((variant) => {
      variant.selected = variant.alias === activeVariant.alias;
      if (variant.diagramSvgSrc) {
        variant.diagramSvgSrc = toEditorPath(variant.diagramSvgSrc);
      } else if (variant.svgSrc) {
        variant.diagramSvgSrc = toEditorPath(variant.svgSrc);
      }
    });
  }

  const baseEditorSvg = activeVariant?.diagramSvgSrc
    || device.diagramSvgSrc
    || device.svgSrc;
  const svgSrc = toEditorPath(baseEditorSvg);
  const label = activeVariant?.label || device.label;
  const aliasForNode = activeVariant?.alias || device.alias;

  return {
    uuid,
    alias: aliasForNode,
    baseAlias: resolvedBaseAlias,
    label,
    svgSrc,
    angle: angleOffset,
    isRotable,
    symbolLabel,
    attributes: cloneDeep(device.attributes),
    handles: cloneDeep(device.handles),
    variants: variants.length > 0 ? variants : undefined,
    variantAlias: activeVariant?.alias || null,
    scaleCm: typeof scaleCm === 'number' && scaleCm > 0 ? scaleCm : DEFAULT_ICON_SCALE_CM,
    isSubdiagram: symbolLabel === 'Subdiagram',
  };
};

export const prepareDragPayload = (
  event,
  group,
  baseAlias,
  angleOffset,
  isRotable,
  symbolLabel,
  variantAlias = null,
  scaleCm = DEFAULT_ICON_SCALE_CM
) => {
  const payload = buildSymbolPayload(
    group,
    baseAlias,
    angleOffset,
    isRotable,
    symbolLabel,
    variantAlias,
    scaleCm
  );

  if (!payload) {
    event.dataTransfer.effectAllowed = 'none';
    return;
  }

  event.dataTransfer.setData('application/reactflow', JSON.stringify(payload));
  event.dataTransfer.effectAllowed = 'move';
  setUniformDragImage(event);
  try {
    window.dispatchEvent(new CustomEvent('sololine:symbol-drag-start', { detail: { payload } }));
  } catch {}
};
